import { Box, Flex, Icon } from '@chakra-ui/react';
import { useField } from 'formik';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Download01,
  FileAttachment04,
  Trash01,
  Upload04,
} from '@kula-ai/icons-core';
import { downloadFileFromUrl } from '@/utils/commonHelpers';
import { KText } from '@/ui-library/components/text';

export const UploadField = ({ item = {} }) => {
  const {
    name,
    maxFiles = 1,
    acceptedFiles = '',
    description,
    download,
    showFiles = true,
    appendFiles = false,
    primaryColor = '',
    handleUnAcceptedFiles = () => {},
    maxSize = undefined,
    convertToBase64 = false,
    gap = '0px',
    replaceOnUpload = false,
    returnFullFileObject = false,
    // Optional async guard run before dropped files are accepted. Receives the newly
    // dropped files and the full prospective list; return false to reject the drop.
    validateFiles = undefined,
  } = item;

  const [files, setFiles] = useState(null);
  const [, { initialValue, value }, helpers] = useField(name);
  const { setValue } = helpers;

  const convertFileToBase64 = async () => {
    try {
      const base64Files = await Promise.all(
        files.map(async (file) => {
          if (!file || !(file instanceof File)) {
            console.warn('Invalid file object:', file);
            return null;
          }
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64Data = reader.result.split(',')[1];

              if (returnFullFileObject) {
                resolve({
                  name: file.name,
                  content_type: file.type,
                  data: base64Data,
                });
              } else {
                resolve({
                  base64: reader.result,
                });
              }
            };
            reader.onerror = () => {
              console.error('Error reading file:', file.name);
              resolve(null);
            };
            reader.readAsDataURL(file);
          });
        })
      );
      const validBase64Files = base64Files.filter((file) => file !== null);

      if (returnFullFileObject) {
        setValue(validBase64Files[0]);
      } else {
        setValue(validBase64Files[0].base64);
      }
    } catch {
      setValue(null);
    }
  };

  useEffect(() => {
    if (!initialValue) return;
    // Edit/API responses provide an existing file as a single object
    // ({ id, url, filename }), while the dropzone flow provides an array.
    // Normalize to an array so existing files always render (filename +
    // download) and the form value stays array-shaped for submit.
    setFiles(Array.isArray(initialValue) ? [...initialValue] : [initialValue]);
  }, [initialValue]);

  useEffect(() => {
    if (convertToBase64) {
      convertFileToBase64();
    } else {
      setValue(files);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  const onDrop = async (selectedFile, unAcceptedFiles) => {
    if (unAcceptedFiles?.length) {
      handleUnAcceptedFiles(unAcceptedFiles);
    }
    const nextFiles =
      appendFiles && value?.length ? [...value, ...selectedFile] : selectedFile;
    // Let the field reject the drop (e.g. size/corrupted checks) before it's accepted.
    if (validateFiles) {
      const isValid = await validateFiles(selectedFile, nextFiles);
      if (!isValid) return;
    }
    setFiles(nextFiles);
  };

  const removeFile = (file) => {
    setFiles((prev) =>
      prev.filter((item) => {
        // For File objects, compare by path
        if (item.path && file.path) {
          return item.path !== file.path;
        }
        // For API file objects, compare by id or url
        if (item.id && file.id) {
          return item.id !== file.id;
        }
        if (item.url && file.url) {
          return item.url !== file.url;
        }
        return item !== file;
      })
    );
  };

  const isDisabled = replaceOnUpload ? false : value?.length >= maxFiles;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: replaceOnUpload ? maxFiles : maxFiles - (value?.length || 0),
    accept: acceptedFiles,
    multiple: maxFiles > 1,
    maxSize: maxSize,
    disabled: isDisabled,
  });

  const renderFilePreview = () => {
    return (
      <Flex flexWrap="wrap" gap={gap}>
        {files?.map((item, index) => {
          // Support both File objects (name) and API file objects (filename)
          const fileName = item?.filename || item?.name;
          const fileUrl = item?.url;

          return (
            <Flex
              key={index}
              p="1em"
              gap="1em"
              border="1px solid var(--kula-dark-secondary-100, #EAEAEA)"
              borderRadius="6px"
              alignItems="center"
              justifyContent="space-between"
              w="100%"
            >
              <Flex gap=".5em" alignItems="center">
                <FileAttachment04 size="1em" className="kula-icon" />
                <Box>{fileName}</Box>
              </Flex>
              <Flex gap="25px" alignItems="center">
                {/* Show download for files with URL (API files) or use download prop */}
                {(fileUrl || download?.url) && (
                  <Download01
                    size="1em"
                    className="kula-icon"
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      downloadFileFromUrl({
                        url: fileUrl || download?.url,
                        filename: fileName,
                        ...download,
                      })
                    }
                  />
                )}
                <Trash01
                  size="1em"
                  className="kula-icon"
                  style={{ cursor: 'pointer' }}
                  onClick={() => removeFile(item)}
                />
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    );
  };

  const renderFileUpload = () => {
    return (
      <Box
        {...getRootProps()}
        flex="1"
        w="100%"
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        borderRadius="8px"
        border="1px dashed"
        borderColor={primaryColor || 'primary-500'}
        opacity={isDisabled ? 0.4 : 'unset'}
      >
        <Flex
          gap="10px"
          justifyContent="center"
          alignItems="center"
          p="40px 10px"
          flexDir="column"
          {...item?.uploadAreaStyles}
        >
          <Flex gap="10px" alignItems="center" id={name}>
            <input {...getInputProps()} />
            <Icon
              as={Upload04}
              width="24px"
              height="24px"
              color={primaryColor || 'primary-600'}
            />
            <Flex>
              <KText textAlign="center" width="max-content">
                {isDragActive ? (
                  <KText as="span" size={'md-medium'} color="secondary-900">
                    Drop your file here to upload
                  </KText>
                ) : (
                  <>
                    <KText as="span" size={'md-medium'} color="secondary-900">
                      Drag-and-Drop file or {` `}
                    </KText>
                    <KText
                      as="span"
                      size={'md-medium'}
                      textDecoration="underline"
                      color={primaryColor || 'primary-600'}
                    >
                      Click to upload
                    </KText>
                  </>
                )}
              </KText>
            </Flex>
          </Flex>
          {description && (
            <KText fontSize="sm" color="secondary-700" textAlign="center">
              {description}
            </KText>
          )}
        </Flex>
      </Box>
    );
  };

  return (
    <Flex flexDir="column" gap="10px" {...item?.style}>
      {files && showFiles && renderFilePreview()}
      {renderFileUpload()}
    </Flex>
  );
};
